// Performance monitoring and optimization utilities
import { onCLS, onINP, onFCP, onLCP, onTTFB } from 'web-vitals'

interface WebVitalsMetric {
  name: string
  value: number
  rating: 'good' | 'needs-improvement' | 'poor'
  delta: number
}

// Web Vitals monitoring
export const initPerformanceMonitoring = () => {
  const sendToAnalytics = (metric: WebVitalsMetric) => {
    // Send to your analytics service
    
    // Example: Send to Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', metric.name, {
        event_category: 'Web Vitals',
        event_label: metric.rating,
        value: Math.round(metric.value),
        non_interaction: true
      })
    }
  }

  // Monitor all Core Web Vitals
  onCLS(sendToAnalytics)
  onINP(sendToAnalytics) // Interaction to Next Paint (replaces FID)
  onFCP(sendToAnalytics)
  onLCP(sendToAnalytics)
  onTTFB(sendToAnalytics)
}

// Performance observer for custom metrics
export const observePerformance = () => {
  if ('PerformanceObserver' in window) {
    // Long Task Observer
    const longTaskObserver = new PerformanceObserver((entryList) => {
      const longTasks = entryList.getEntries()
      longTasks.forEach((task) => {
        if (task.duration > 50) {
          // Long task detected - could send to analytics
        }
      })
    })
    
    try {
      longTaskObserver.observe({ entryTypes: ['longtask'] })
    } catch {
      // Long tasks not supported
    }

    // Layout Shift Observer
    const layoutShiftObserver = new PerformanceObserver((entryList) => {
      const shifts = entryList.getEntries()
      shifts.forEach((shift: unknown) => {
        const layoutShift = shift as { hadRecentInput?: boolean; value?: number }
        if (!layoutShift.hadRecentInput && (layoutShift.value || 0) > 0.1) {
          // Layout shift detected - could send to analytics
        }
      })
    })

    try {
      layoutShiftObserver.observe({ entryTypes: ['layout-shift'] })
    } catch {
      // Layout shift not supported
    }
  }
}

// Image optimization utilities
export const optimizeImage = (src: string, width?: number, height?: number) => {
  // Add responsive image parameters
  const url = new URL(src)
  if (width) url.searchParams.set('w', width.toString())
  if (height) url.searchParams.set('h', height.toString())
  url.searchParams.set('q', '85') // Quality
  url.searchParams.set('f', 'webp') // Format
  return url.toString()
}

// Lazy loading utility
export const createIntersectionObserver = (
  callback: (entries: IntersectionObserverEntry[]) => void,
  options?: IntersectionObserverInit
) => {
  if ('IntersectionObserver' in window) {
    return new IntersectionObserver(callback, {
      rootMargin: '50px',
      threshold: 0.1,
      ...options
    })
  }
  return null
}

// Memory usage monitoring
export const monitorMemoryUsage = () => {
  if ('memory' in performance) {
    const memory = (performance as { memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory
    if (memory) {
      const memoryInfo = {
        used: Math.round(memory.usedJSHeapSize / 1048576), // MB
        total: Math.round(memory.totalJSHeapSize / 1048576), // MB
        limit: Math.round(memory.jsHeapSizeLimit / 1048576) // MB
    }
    
    if (memoryInfo.used / memoryInfo.limit > 0.8) {
      // High memory usage detected - could send to analytics
    }
    
      return memoryInfo
    }
  }
  return null
}

// Bundle analysis helper
export const analyzeBundleSize = () => {
  // Bundle analysis functionality for future use
  return { bundleCount: 0 }
}

// Performance timing
export const measureTiming = (name: string) => {
  return {
    start: () => performance.mark(`${name}-start`),
    end: () => {
      performance.mark(`${name}-end`)
      performance.measure(name, `${name}-start`, `${name}-end`)
      const measure = performance.getEntriesByName(name)[0]
      return measure.duration
    }
  }
}

// Preload critical resources
export const preloadCriticalResources = () => {
  const criticalResources = [
    // Add your critical font files
    '/fonts/inter-var.woff2',
    // Add critical images
    '/images/logo.webp'
  ]

  criticalResources.forEach(src => {
    const link = document.createElement('link')
    link.rel = 'preload'
    link.href = src
    
    if (src.includes('.woff2')) {
      link.as = 'font'
      link.type = 'font/woff2'
      link.crossOrigin = 'anonymous'
    } else if (src.includes('.webp') || src.includes('.jpg') || src.includes('.png')) {
      link.as = 'image'
    }
    
    document.head.appendChild(link)
  })
}

export default {
  initPerformanceMonitoring,
  observePerformance,
  optimizeImage,
  createIntersectionObserver,
  monitorMemoryUsage,
  analyzeBundleSize,
  measureTiming,
  preloadCriticalResources
}